import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";

type ColumnOrderOptions = {
  pageKey: string;
  userId: number | string | null | undefined;
  columns: string[];
  fixedColumns?: string[];
};

function normalizeOrder(saved: unknown, columns: string[], fixedColumns: string[]) {
  const valid = new Set(columns);
  const fixed = fixedColumns.filter((column) => valid.has(column));
  const savedColumns = Array.isArray(saved)
    ? saved.filter((column): column is string => typeof column === "string" && valid.has(column) && !fixed.includes(column))
    : [];
  const missing = columns.filter((column) => !fixed.includes(column) && !savedColumns.includes(column));
  return [...fixed, ...savedColumns, ...missing];
}

function loadOrder(storageKey: string, columns: string[], fixedColumns: string[]) {
  if (typeof window === "undefined") return normalizeOrder([], columns, fixedColumns);
  try {
    return normalizeOrder(JSON.parse(window.localStorage.getItem(storageKey) || "[]"), columns, fixedColumns);
  } catch {
    return normalizeOrder([], columns, fixedColumns);
  }
}

/**
 * Mantém a ordem visual de uma tabela no navegador, separada por usuário e tela.
 * As colunas fixas continuam sempre à esquerda e não recebem o handle de arraste.
 */
export function useColumnOrder({ pageKey, userId, columns, fixedColumns = [] }: ColumnOrderOptions) {
  const columnSignature = columns.join("|");
  const fixedSignature = fixedColumns.join("|");
  const storageKey = `warehouse:column-order:v1:${pageKey}:${String(userId ?? "anonymous")}`;
  const [order, setOrder] = useState(() => loadOrder(storageKey, columns, fixedColumns));
  const activeColumnRef = useRef<string | null>(null);
  const currentKeyRef = useRef(storageKey);

  useEffect(() => {
    const next = currentKeyRef.current !== storageKey
      ? loadOrder(storageKey, columns, fixedColumns)
      : normalizeOrder(order, columns, fixedColumns);
    currentKeyRef.current = storageKey;
    setOrder((current) => current.join("|") === next.join("|") ? current : next);
    // columnSignature and fixedSignature intentionally detect column-set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, columnSignature, fixedSignature]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, JSON.stringify(normalizeOrder(order, columns, fixedColumns)));
    }
    // columnSignature and fixedSignature intentionally detect column-set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, order, columnSignature, fixedSignature]);

  const orderedColumns = useMemo(
    () => normalizeOrder(order, columns, fixedColumns),
    [order, columnSignature, fixedSignature],
  );

  function moveColumn(source: string, target: string) {
    if (source === target || fixedColumns.includes(source) || fixedColumns.includes(target)) return;
    setOrder((current) => {
      const normalized = normalizeOrder(current, columns, fixedColumns);
      const sourceIndex = normalized.indexOf(source);
      const targetIndex = normalized.indexOf(target);
      if (sourceIndex < 0 || targetIndex < 0) return normalized;
      const next = [...normalized];
      next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, source);
      return next;
    });
  }

  function getDragHandleProps(column: string) {
    if (fixedColumns.includes(column)) return {};
    return {
      draggable: true,
      onDragStart: (event: DragEvent<HTMLElement>) => {
        activeColumnRef.current = column;
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", column);
      },
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (activeColumnRef.current && activeColumnRef.current !== column) event.preventDefault();
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        const source = activeColumnRef.current || event.dataTransfer.getData("text/plain");
        if (source) moveColumn(source, column);
        activeColumnRef.current = null;
      },
      onDragEnd: () => {
        activeColumnRef.current = null;
      },
    };
  }

  return { orderedColumns, getDragHandleProps };
}

export function columnDragHandleClassName(isFixed = false) {
  return isFixed
    ? ""
    : "ml-1 inline-flex cursor-grab touch-none select-none text-muted-foreground hover:text-foreground active:cursor-grabbing";
}
